import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { Sparkles } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_f59c783b-4061-4c90-8631-f537af31e122/artifacts/nm25t9qa_Calcutta%20Logo.png";
const HERO = "https://images.pexels.com/photos/7781900/pexels-photo-7781900.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [organization, setOrganization] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register({ email, password, name, organization });
      }
      toast.success("Welcome to Calcutta");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const enterDemo = () => {
    // Hard navigation to /demo so we escape any stale cached JS on this page
    window.location.href = "/demo";
  };

  const fillDemo = () => {
    setEmail("demo@calcutta.ip");
    setPassword("Demo@1234");
    setMode("login");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left — Form */}
      <div className="flex items-center justify-center px-6 md:px-12 py-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <img src={LOGO} alt="Calcutta" className="w-11 h-11 rounded-sm" />
            <div>
              <div className="font-serif text-2xl font-semibold tracking-tight">Calcutta</div>
              <div className="eyebrow text-[10px]">Global IP Management</div>
            </div>
          </div>

          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-tight">
            {mode === "login" ? "Welcome back." : "Claim your IP."}
          </h1>
          <p className="mt-3 text-slate-600 text-sm md:text-base leading-relaxed">
            {mode === "login"
              ? "Sign in to manage patents, trademarks, copyrights and licensing across 170+ jurisdictions."
              : "Create a workspace and start registering global IP in minutes."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <div>
                  <label className="eyebrow block mb-1.5">Full name</label>
                  <input
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="register-name-input"
                    className="w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#B33A3A] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-1.5">Organization</label>
                  <input
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    data-testid="register-org-input"
                    className="w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#B33A3A] focus:border-transparent"
                  />
                </div>
              </>
            )}
            <div>
              <label className="eyebrow block mb-1.5">Email</label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="email-input"
                className="w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#B33A3A] focus:border-transparent"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Password</label>
              <input
                required
                type="password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="password-input"
                className="w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#B33A3A] focus:border-transparent"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full bg-[#B33A3A] hover:bg-[#A02538] disabled:opacity-60 text-white rounded-sm py-2.5 font-medium text-sm transition-colors"
            >
              {loading ? "Working…" : mode === "login" ? "Sign in" : "Create account"}
            </button>

            <button
              type="button"
              onClick={enterDemo}
              data-testid="enter-demo-btn"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm py-2.5 font-medium text-sm transition-colors flex items-center justify-center gap-2"
            >
              <Sparkles size={14} /> Enter Demo Workspace
            </button>

            <button
              type="button"
              onClick={fillDemo}
              data-testid="use-demo-btn"
              className="w-full border border-slate-300 hover:border-[#B33A3A] hover:text-[#B33A3A] rounded-sm py-2 font-medium text-xs transition-colors text-slate-600"
            >
              Fill demo credentials
            </button>
          </form>

          <div className="mt-6 text-sm text-slate-600 flex items-center justify-between gap-3 flex-wrap">
            {mode === "login" ? (
              <span>
                New here?{" "}
                <button
                  onClick={() => setMode("register")}
                  className="text-[#B33A3A] font-medium hover:underline"
                  data-testid="toggle-register"
                >
                  Create an account
                </button>
              </span>
            ) : (
              <span>
                Already registered?{" "}
                <button
                  onClick={() => setMode("login")}
                  className="text-[#B33A3A] font-medium hover:underline"
                  data-testid="toggle-login"
                >
                  Sign in
                </button>
              </span>
            )}
            {mode === "login" && (
              <Link to="/forgot-password" className="text-slate-500 hover:text-[#B33A3A] text-sm font-medium" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Right — Hero */}
      <div
        className="hidden lg:flex relative bg-[#B33A3A] overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${HERO})` }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div>
            <div className="eyebrow text-white/70">Trusted by inventors worldwide</div>
            <h2 className="font-serif text-5xl leading-tight mt-4 max-w-md">
              From idea to income — the IP lifecycle, unified.
            </h2>
          </div>
          <div className="space-y-3 max-w-md">
            {[
              "File patents, trademarks & copyrights across 170+ jurisdictions",
              "AI-powered valuation and prior-art similarity detection",
              "Licensing marketplace with escrow-backed deals",
            ].map((line) => (
              <div key={line} className="flex items-start gap-3 text-sm text-white/90">
                <span className="w-1 h-1 rounded-full bg-white mt-2 shrink-0" />
                <span>{line}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
